import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";
import { onb } from "@/lib/onb";
import { errText } from "@/lib/http";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { Btn } from "@/components/mh/controls";

const STATE = { pending: { c: "#3A3A46", t: "queued" }, running: { c: "#00E5FF", t: "tailoring…" }, done: { c: "#D4FF3A", t: "tailored" }, failed: { c: "#FF3B5C", t: "failed" } };

export const TailorPanel = ({ st, canEdit }) => {
  const job = st.job;
  const doneIv = st.interviews.departments.filter((d) => d.status === "done").length;
  const running = job?.status === "running";
  const start = async (overwrite) => { try { await onb.tailor(null, overwrite); toast.success("Tailoring started. Watch the map light up"); } catch (e) { toast.error(errText(e)); } };
  return (
    <Panel cls="p-5 sm:p-8 space-y-6" testId="onb-tailor">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div><Eyebrow>Step 4 · Tailor</Eyebrow><h3 className="font-display text-3xl sm:text-4xl mt-1">Rebuild 140 roles in your terms</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Each role is scored for fit, rewritten with your products, voice, tools and rules, valued from your numbers, given an owner, and placed in a 3-wave rollout. Departments without an interview are tailored from company info and marked "Needs input".</p></div>
        {canEdit && (
          <div className="flex flex-col items-start sm:items-end gap-2">
            <Btn variant="primary" onClick={() => start(false)} disabled={running || doneIv === 0} testId="onb-tailor-btn"><Wand2 className="w-4 h-4" />{running ? "Tailoring…" : job ? "Re-tailor my atlas" : "Tailor my atlas"}</Btn>
            {job && !running && <button onClick={() => start(true)} className="font-mono text-[10px] text-muted-foreground underline" data-testid="onb-tailor-overwrite">Re-tailor and overwrite hand edits</button>}
            {doneIv === 0 && <span className="font-mono text-[10px] text-[#FFB020]" data-testid="onb-tailor-needs">Finish at least one department interview first</span>}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="onb-tailor-grid">
        {st.interviews.departments.map((d, i) => {
          const s = STATE[job?.depts?.[d.slug]] || (job ? { c: "#26262E", t: "not in this run" } : STATE.pending);
          const lit = job?.depts?.[d.slug] === "done";
          return (
            <div key={d.slug} className={`relative rounded-2xl border p-4 aspect-square flex flex-col justify-between overflow-hidden transition-[border-color,box-shadow] duration-700 ${job?.depts?.[d.slug] === "running" ? "mh-blink" : ""}`}
              style={{ borderColor: lit ? `${d.hex}aa` : "rgba(255,255,255,0.07)", boxShadow: lit ? `0 0 40px -10px ${d.hex}` : "none", animationDelay: `${i * 80}ms` }} data-testid={`onb-tailor-dept-${d.slug}`}>
              <span className="absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl transition-opacity duration-700" style={{ background: d.hex, opacity: lit ? 0.35 : 0.05 }} />
              <span className="w-2 h-2 rotate-45" style={{ background: d.hex }} />
              <div><div className="text-sm font-medium">{d.name}</div>
                <div className="font-mono text-[10px] mt-0.5" style={{ color: s.c }}>{s.t}</div>
                {d.status !== "done" && <div className="font-mono text-[9px] text-[#FFB020] mt-0.5">needs input</div>}</div>
            </div>
          );
        })}
      </div>
      {job?.error && <p className="text-sm text-[#FF3B5C]" data-testid="onb-tailor-error">{job.error}</p>}
      {job && ["done", "partial"].includes(job.status) && (
        <Link to="/your-map" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#D4FF3A] text-black font-semibold text-sm hover:bg-[#e2ff73] transition-colors" data-testid="onb-see-map">See your map →</Link>
      )}
    </Panel>
  );
};
